import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "@/shared/i18n";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Button } from "@/shared/ui/button";
import { ReceivableStatusBadge } from "@/features/receivables/components";
import { formatCurrency, formatPercent } from "@/shared/utils/format";
import { formatDateShort, monthKey } from "@/shared/utils/date";
import type { FinancialOverviewSummaryCreatorReceivable } from "../types";

interface Props {
  receivables: FinancialOverviewSummaryCreatorReceivable[];
  currency: string;
}

interface MonthBucket {
  key: string;
  label: string;
  sort: number;
  items: FinancialOverviewSummaryCreatorReceivable[];
  totalGross: number;
  totalShare: number;
}

export function CreatorReceivablesByMonth({ receivables, currency }: Props) {
  const { t, i18n } = useTranslation("admin-financial-overview");

  const grouped = useMemo<MonthBucket[]>(() => {
    const map = new Map<string, MonthBucket>();
    for (const r of receivables) {
      const { key, label, sort } = monthKey(r.relatedDate, i18n.language);
      const bucket = map.get(key) ?? {
        key,
        label,
        sort,
        items: [],
        totalGross: 0,
        totalShare: 0,
      };
      bucket.items.push(r);
      bucket.totalGross += r.grossAmount;
      bucket.totalShare += r.shareAmount;
      map.set(key, bucket);
    }
    return Array.from(map.values()).sort((a, b) => b.sort - a.sort);
  }, [receivables, i18n.language]);

  // Default: todos os meses colapsados. Set guarda só os que o usuário expandiu.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleMonth = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="px-6 py-4 space-y-3">
      {grouped.map((g) => {
        const isOpen = expanded.has(g.key);
        const countLabel =
          g.items.length === 1
            ? t("monthReceivablesOne", { count: g.items.length })
            : t("monthReceivablesOther", { count: g.items.length });
        return (
          <div
            key={g.key}
            className="rounded-md border border-white/[0.06] bg-white/[0.02]"
          >
            <div
              className={
                "flex items-center justify-between px-4 py-2" +
                (isOpen ? " border-b border-white/[0.06]" : "")
              }
            >
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => toggleMonth(g.key)}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? t("collapseMonth") : t("expandMonth")}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
                <span className="font-medium">{g.label}</span>
              </div>
              <div className="flex gap-6 text-sm">
                <span className="text-muted-foreground">{countLabel}</span>
                <span className="font-mono text-muted-foreground">
                  {t("monthGrossLabel")}{" "}
                  <span className="text-foreground">
                    {formatCurrency(currency, g.totalGross)}
                  </span>
                </span>
                <span className="font-mono text-muted-foreground">
                  {t("monthShareLabel")}{" "}
                  <span className="text-foreground">
                    {formatCurrency(currency, g.totalShare)}
                  </span>
                </span>
              </div>
            </div>
            {isOpen && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("colStore")}</TableHead>
                    <TableHead>{t("colStatus")}</TableHead>
                    <TableHead>{t("colPaidOn")}</TableHead>
                    <TableHead className="text-right">
                      {t("colGross")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("colPercentage")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("colShare")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.items.map((r) => (
                    <TableRow key={r.receivableId}>
                      <TableCell className="font-medium">{r.store}</TableCell>
                      <TableCell>
                        <ReceivableStatusBadge status={r.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateShort(r.paidDate)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(currency, r.grossAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPercent(r.sharePercentage, i18n.language)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(currency, r.shareAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        );
      })}
    </div>
  );
}
